import { useMemo } from 'react'
import { Navigate, Outlet } from 'react-router-dom'

import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { convertAddressPrefix } from '@/helpers/cosmos'
import { RoutePaths } from '@/routes'
import type { Validator } from '@/types'
import { UiSkeleton } from '@/ui/UiSkeleton'

import { useValidatorsList } from '../hooks/useValidatorsList'

export default function GovernanceGuard() {
  const { address, isWalletConnected, status } = useKeplrWallet()
  const { validators, isLoading: isValidatorsLoading } = useValidatorsList()

  const userValoper = useMemo(
    () => (address ? convertAddressPrefix(address) : null),
    [address],
  )

  const isValidator = useMemo(() => {
    if (!userValoper || !validators.length) return false
    return validators.some((v: Validator) => v.operator_address === userValoper)
  }, [validators, userValoper])

  if (status === 'Connecting' || status === 'Disconnected') {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <div className='flex flex-col items-center gap-4'>
          <UiSkeleton className='h-12 w-64' />
          <UiSkeleton className='h-8 w-48' />
        </div>
      </div>
    )
  }

  if (!isWalletConnected || !address) {
    return (
      <Navigate
        to={RoutePaths.Root}
        replace
        state={{
          error: 'Please connect your wallet to access governance panel',
        }}
      />
    )
  }

  if (isValidatorsLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <div className='flex flex-col items-center gap-4'>
          <UiSkeleton className='h-12 w-64' />
          <UiSkeleton className='h-8 w-48' />
        </div>
      </div>
    )
  }

  if (!isValidator) {
    return <Navigate to={RoutePaths.Root} replace />
  }

  return <Outlet />
}
